import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { logout as authReduxLogout } from "@/store/slices/authSlice";
import { clearUser } from "@/store/slices/userSlice";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Menu,
  MenuItem,
  useMediaQuery,
  IconButton,
  Typography,
  TextField,
  Badge,
  Tooltip,
  Dialog,
  DialogContent,
  Autocomplete,
  DialogActions,
  DialogTitle,
  Drawer,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import PersonIcon from "@mui/icons-material/Person";
import LanguageIcon from "@mui/icons-material/Language";
import CloseIcon from "@mui/icons-material/Close";
import { Warning as WarningIcon } from "@mui/icons-material";
import CustomerBadge from "./CustomerBadge";
import { menuItems } from "@/utils/menus";
import { useTranslation } from "react-i18next";
import SelectionPopup from "./customers/selection-popup";
import SetupLoaderComponent from "./SetupLoader";
import { useSelector } from "react-redux";
import { persistor, RootState, store } from "@/store/store";
import UnreadNotifications from "@/components/notifications/UnreadNotification";
import { setLanguage } from "@/store/slices/languageSlice";
import {
  setUnauthorizedModalOpen,
  resetUnauthorized,
} from "../store/slices/unauthorizedSlice";
import { resetUnauthorizedModalFlag } from "../services/http-client";
import { usePermissions } from "@/hooks/usePermissions";
import { useIdleTimeout } from "@/hooks/useIdleTimeout";

// Idle session timeout (in minutes), configurable via env. Defaults to 120 (2 hours).
const IDLE_TIMEOUT_MS =
  (Number(import.meta.env.VITE_IDLE_TIMEOUT_MINUTES) || 120) * 60 * 1000;

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAuthenticated, loginWithRedirect } = useAuth0();
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCustomerPanelOpen, setIsCustomerPanelOpen] =
    useState<boolean>(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [modalType, setModalType] = useState<"SITE" | "LOCATION" | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [langDialogOpen, setLangDialogOpen] = useState(false);
  const locale = navigator.language;
  const [selectedLang, setSelectedLang] = useState(() => {
    const savedLang = localStorage.getItem("selectedLanguage");
    return savedLang || i18n.language || "en";
  });
  const { data: userData } = useAppSelector((state: RootState) => state.user);
  const currentLang = useAppSelector((state) => state.language.currentLang);
  const { isUnauthorizedModalOpen, message } = useSelector(
    (state: any) => state.unauthorized,
  );

  const storeCode = useSelector((state: RootState) => state.site.storeCode);
  const cartName =
    storeCode === "fctl" ? t("finalControl") : t("measurementInstrumentation");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const openModal = (type: "SITE" | "LOCATION") => setModalType(type);

  const showCart = /^\/devices\/[a-zA-Z0-9-]+$/.test(location.pathname);
 
  const { getFeatureActions } = usePermissions();
  const locationsPermissions = getFeatureActions(
    "Location",
    "Show Location Tab",
    "Location Page",
  );

  const notificationsPermissions = getFeatureActions(
    "Navigation",
    "View Notification",
    "Global Task Queue",
  );

  const navigationPermissions = getFeatureActions(
    "Navigation",
    "Global Search",
    "Search by Serial Number",
  );

  const viewSiteDocTabPermission = getFeatureActions(
    "Site Documentation",
    "View Site Documentation Tab",
    "Site Documentation Page",
  );

  const valveConditionMonitoringPermission = getFeatureActions("VCM","View VCM Tab","VCM Tab");

  // Filter menu items based on permissions
  const filteredMenuItems = useMemo(() => {
    return menuItems.filter((item) => {
      // Check if this is the locations menu item
      if (item.key === "locations") {
        return locationsPermissions.canView;
      } else if (item.key === "site-documentation") {
        return viewSiteDocTabPermission.canView;
      } else  if(item.key === "valve-condition-monitoring") {
        return valveConditionMonitoringPermission.canView;
      }
      // All other menu items are shown by default
      return true;
    });
  }, [locationsPermissions.canView, viewSiteDocTabPermission.canView, valveConditionMonitoringPermission.canView]);

  // Idle session timeout: show "Session Expired" modal after configured period
  // of inactivity (default 2 hours). Disabled when user is not authenticated
  // or when modal is already open.
  const handleIdle = useCallback(() => {
    console.warn(
      `User idle for ${IDLE_TIMEOUT_MS / 60000} minutes - triggering session timeout`,
    );
    dispatch(setUnauthorizedModalOpen(true));
  }, [dispatch]);

  useIdleTimeout({
    onIdle: handleIdle,
    timeoutMs: IDLE_TIMEOUT_MS,
    enabled: isAuthenticated && !isUnauthorizedModalOpen,
  });

  // Check overflow when component mounts and on resize
  useEffect(() => {
    const checkAndUpdateArrows = () => {
      if (scrollContainerRef.current && !isMobile) {
        const { scrollLeft, scrollWidth, clientWidth } =
          scrollContainerRef.current;
        const hasOverflow = scrollWidth > clientWidth + 5;
        setShowLeftArrow(hasOverflow && scrollLeft > 15);
        setShowRightArrow(
          hasOverflow && scrollLeft < scrollWidth - clientWidth - 15,
        );
      }
    };

    // Check on mount and when menu items change
    const timer = setTimeout(checkAndUpdateArrows, 100);

    // Check on window resize
    window.addEventListener("resize", checkAndUpdateArrows);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkAndUpdateArrows);
    };
  }, [isMobile, filteredMenuItems]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    dispatch(resetUnauthorized());
    resetUnauthorizedModalFlag(); // Reset the flag so modal can show again after re-login
    store.dispatch({ type: "LOGOUT" }); // Clears Redux state
    persistor.purge(); // clears local and session storage

    // dispatch(authReduxLogout())
    // dispatch(clearUser())
    await logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const navigateTo = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const handleDrawerToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleCustomerPanel = () => {
    setIsCustomerPanelOpen((prev) => !prev);
  };

  const closeCustomerPanel = () => {
    setIsCustomerPanelOpen(false);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -200,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 200,
        behavior: "smooth",
      });
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      const hasOverflow = checkOverflow();
      setShowLeftArrow(hasOverflow && scrollLeft > 15); // 15px threshold
      setShowRightArrow(
        hasOverflow && scrollLeft < scrollWidth - clientWidth - 15,
      );
    }
  };

  const checkOverflow = () => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth } = scrollContainerRef.current;
      return scrollWidth > clientWidth + 5; // 5px tolerance
    }
    return false;
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearch = () => {
    const trimmed = searchValue.trim();
    if (trimmed.length < 4 || trimmed.length > 20) {
      return;
    }

    navigate(`/search-results/${encodeURIComponent(trimmed)}`);
    setSearchValue("");
  };

  const handleCartClick = () => {
    if (storeCode) {
      const cartUrl = getCartUrl(storeCode);
      window.open(cartUrl, "_blank");
    }
  };

  const getCartUrl = (store: string): string => {
    const baseUrl = import.meta.env.VITE_EMERSON_CART_URL;

    if (store === "fctl") {
      return `${baseUrl}/final-control/cart/auth/login?prompt=none&returnTo=${baseUrl}/final-control/cart`;
    } else if (store === "msol") {
      return `${baseUrl}/measurement-instrumentation/cart/auth/login?prompt=none&returnTo=${baseUrl}/measurement-instrumentation/cart`;
    }

    return `${baseUrl}/cart`;
  };

  const handleLangDialogOpen = () => setLangDialogOpen(true);
  const handleLangDialogClose = () => setLangDialogOpen(false);
  const languages = [
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
    { code: "de", label: "Deutsch" },
    { code: "it", label: "Italiano" },
    { code: "ja", label: "日本語" },
    { code: "ko", label: "한국어" },
    { code: "pt", label: "Português" },
    { code: "ch", label: "中文" },
  ];
  const handleLangSelect = () => {
    handleLangDialogClose();
    console.log("NavBar: languar", selectedLang);
    dispatch(setLanguage(selectedLang));
    // if (selectedLang && i18n.language !== selectedLang) {
    //   i18n.changeLanguage(selectedLang);
    //   localStorage.setItem('selectedLanguage', selectedLang);
    // }
  };

  return (
    <>
      <AppBar position="static" elevation={1} className="appbar-navbar">
        <Toolbar
          className="appbar-toolbar"
          sx={{ justifyContent: "space-between", px: 2 }}
        >
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              className="navbar-mobile-menu-button"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Left Section - Customer Selector & Navigation */}
          <Box
            className="appbar-customer-menu"
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              gap: 1,
              minWidth: 0, // Allows flex items to shrink
            }}
          >
            {/* Customer Selector - Desktop & Tablet */}
            {!isMobile && (
              <Box
                className="navbar-customer-selector-container"
                sx={{ flexShrink: 0 }}
              >
                <Box
                  className="customer-menu-item navbar-customer-selector-box"
                  onClick={toggleCustomerPanel}
                >
                  <LocationOnOutlinedIcon className="theme-darkgrey-color" />
                  <Typography variant="body2" className="navbar-customer-text">
                    {t("customer", "CUSTOMER")}
                  </Typography>
                  <ExpandMoreIcon
                    className={`appbar-expand navbar-customer-expand-icon ${
                      isCustomerPanelOpen ? "open" : "closed"
                    }`}
                  />
                </Box>
              </Box>
            )}

            {/* Navigation Items - Desktop & Tablet with Scrolling */}
            {!isMobile && (
              <Box
                className="navbar-navigation-container"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  mx: 1, // Add horizontal margin for spacing
                }}
              >
                {/* Left Arrow */}
                {showLeftArrow && (
                  <IconButton
                    onClick={scrollLeft}
                    className="navbar-tab-arrow-left"
                  >
                    <ArrowBackIosIcon className="navbar-arrow-left" />
                  </IconButton>
                )}

                {/* Scrollable Menu Container */}
                <Box
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className={`navbar-scrollable-menu ${
                    isTablet ? "tablet" : "desktop"
                  }`}
                  style={{
                    paddingLeft: showLeftArrow ? "32px" : "4px",
                    paddingRight: showRightArrow ? "32px" : "4px",
                  }}
                >
                  {filteredMenuItems.map((item) => (
                    <Button
                      className={`${location.pathname.includes(item.path) ? "active-navbar navbarbtn" : "navbarbtn"} navbar-menu-button ${isTablet ? "tablet" : "desktop"}`}
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      sx={{ textAlign: "center", justifyContent: "center" }}
                    >
                      <span>{t(item.key, item.label).toUpperCase()}</span>
                    </Button>
                  ))}
                </Box>

                {/* Right Arrow */}
                {showRightArrow && (
                  <IconButton
                    onClick={scrollRight}
                    className="navbar-tab-arrow-right"
                  >
                    <ArrowForwardIosIcon className="navbar-arrow-right" />
                  </IconButton>
                )}
              </Box>
            )}

            {/* Mobile Title */}
            {isMobile && (
              <Typography variant="h6" className="navbar-devices-title">
                {t("devices", "DEVICES")}
              </Typography>
            )}
          </Box>

          <Box
            className="appbar-right-section"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
            }}
          >
            {/* Search */}
            {navigationPermissions.canView && (
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                  width: "180px",
                }}
                className="navbar-search-container"
              >
                <TextField
                  placeholder={t("serialNumber", "Serial Number")}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  variant="standard"
                  className="navbar-search"
                  size="small"
                  onKeyDown={handleKeyPress}
                  inputProps={{
                    maxLength: 20,
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={handleSearch}
                        sx={{
                          p: "4px",
                          color: "#262728",
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "#262728",
                          },
                          "&:focus": {
                            outline: "none",
                          },
                          "&:focus-visible": {
                            outline: "none",
                            boxShadow: "none",
                          },
                        }}
                      >
                        <SearchIcon />
                      </IconButton>
                    ),
                  }}
                  InputLabelProps={{
                    sx: {
                      "::placeholder": { color: "#262728" },
                      color: "#262728",
                    },
                  }}
                />
              </Box>
            )}

            {/* Notification Icons */}
            {isAuthenticated && (
              <Box
                className="navbar-notifications-container"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  justifyContent: "flex-end",
                  flexShrink: 0,
                }}
              >
                {showCart ? (
                  <IconButton
                    className="navbar-icon-btn"
                    onClick={handleCartClick}
                    title={t("goToCartWithName", { cartName })}
                  >
                    <ShoppingCartIcon />
                  </IconButton>
                ) : (
                  <Badge
                    badgeContent={99}
                    sx={{ display: "none" }}
                    className="navbar-badge-cart"
                  >
                    <IconButton className="navbar-icon-btn">
                      <ShoppingBagOutlinedIcon />
                    </IconButton>
                  </Badge>
                )}
                {/* User Profile Icon with Tooltip and Menu */}
                <Tooltip title={userData?.email ?? ""}>
                  <IconButton onClick={handleMenuOpen}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: "#e0e7ef",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#374151",
                      }}
                      className="navbar-profile-avatar"
                    >
                      {(() => {
                        if (!userData?.email)
                          return <PersonIcon className="navbar-profile-icon" />;
                        const [namePart] = userData.email.split("@");
                        const nameSegments = namePart.split(/[._-]/);
                        const firstName = nameSegments[0] || "";
                        const lastName =
                          nameSegments.length > 1
                            ? nameSegments[nameSegments.length - 1]
                            : "";
                        return (
                          <>
                            {firstName.charAt(0).toUpperCase()}
                            {lastName ? lastName.charAt(0).toUpperCase() : ""}
                          </>
                        );
                      })()}
                    </Box>
                  </IconButton>
                </Tooltip>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                    cursor: "pointer",
                    color: "#374151",
                    fontWeight: 500,
                    userSelect: "none",
                    flexShrink: 0,
                    ml: 1,
                  }}
                  onClick={handleLangDialogOpen}
                  className="navbar-language-selector"
                >
                  <LanguageIcon sx={{ color: "#374151", fontSize: 20 }} />
                  <span
                    style={{
                      color: "#374151",
                      fontWeight: 500,
                      fontSize: "10px",
                      letterSpacing: 1,
                      marginLeft: 6,
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {i18n?.language?.toUpperCase()}
                  </span>
                </Box>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  className="navbar-profile-menu"
                  disableScrollLock={true}
                >
                  <MenuItem>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight="bold"
                    >
                      {userData?.email ?? ""}
                    </Typography>
                  </MenuItem>
                  {userData?.superadmin ? (
                    <MenuItem>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        onClick={() => {
                          handleMenuClose();
                          navigate("/admin");
                        }}
                      >
                        {t("admin", "Admin")}
                      </Typography>
                    </MenuItem>
                  ) : null}

                  <Button
                    onClick={handleLogout}
                    color="success"
                    variant="text"
                    className="navbar-logout-button"
                  >
                    {t("logout", "Logout")}
                  </Button>
                </Menu>
                {notificationsPermissions.canView && <UnreadNotifications />}
              </Box>
            )}
            {!isAuthenticated && (
              <Button
                onClick={() => {
                  // Trigger Auth0 login with return to current page
                  loginWithRedirect({
                    appState: {
                      returnTo: location.pathname + location.search,
                    },
                  });
                }}
                sx={{ backgroundColor: "#00573D", color: "#ffffff" }}
                variant="contained"
                className="navbar-login-button"
              >
                {t("login", "Login")}
              </Button>
            )}
          </Box>
        </Toolbar>
        <SelectionPopup
          closeCustomerPanel={closeCustomerPanel}
          openModal={openModal}
          open={isCustomerPanelOpen}
        />

        <SetupLoaderComponent></SetupLoaderComponent>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleDrawerToggle}
        classes={{ paper: "navbar-drawer-paper" }}
      >
        <Box className="navbar-drawer-container">
          {/* Drawer Header */}
          <Box className="navbar-drawer-header">
            <Box
              className="navbar-drawer-customer-section"
              onClick={() => {
                toggleCustomerPanel();
                handleDrawerToggle();
              }}
            >
              <LocationOnOutlinedIcon className="navbar-drawer-customer-icon" />
              <Typography className="navbar-drawer-customer-text">
                {t("customer", "CUSTOMER")}
              </Typography>
              <ExpandMoreIcon
                className="navbar-drawer-expand-icon"
                sx={{
                  transform: isCustomerPanelOpen
                    ? "rotate(180deg)"
                    : "rotate(0)",
                  transition: "transform 0.3s",
                }}
              />
            </Box>
            <IconButton
              onClick={handleDrawerToggle}
              className="navbar-drawer-close-button"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider className="navbar-drawer-divider" />

          {/* Navigation Menu Items */}
          <List sx={{ paddingTop: 2 }}>
            {filteredMenuItems.map((item) => (
              <ListItem
                key={item.path}
                className={`navbar-drawer-list-item ${
                  location.pathname.includes(item.path) ? "active" : ""
                }`}
              >
                <Box className="navbar-drawer-list-item-content">
                  <Button
                    fullWidth
                    onClick={() => {
                      navigate(item.path);
                      handleDrawerToggle();
                    }}
                    className="navbar-drawer-menu-button"
                  >
                    <Typography
                      className={`navbar-drawer-menu-text ${
                        location.pathname.includes(item.path) ? "active" : ""
                      }`}
                    >
                      {item.label.toUpperCase()}
                    </Typography>
                  </Button>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Language Dialog */}
      <Dialog
        open={langDialogOpen}
        onClose={handleLangDialogClose}
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 0,
            mt: 4,
            width: 320,
            maxWidth: "90vw",
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "center", // Center vertically
            justifyContent: "center", // Center horizontally
            display: "flex", // Ensure flex for centering
            p: 0,
            m: 0,
            minHeight: "100vh", // Full viewport height for vertical centering
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "#f3f4f6",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 0,
            m: 0,
            minHeight: 28,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 12,
              p: 0.8,
              m: 0,
              flex: 1,
            }}
          >
            {/* <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, mt: 1, textTransform: 'uppercase' }}>
                Language Selection
              </Typography> */}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleLangDialogClose}
            sx={{
              p: 0.8,
              m: 0,
            }}
            size="small"
          >
            <CloseIcon sx={{ fontSize: 18, color: "#000" }} />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, mt: 1 }}>
            {t("selectLanguage", "SELECT A LANGUAGE")}
          </Typography>
          <Autocomplete
            options={languages}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            value={languages.find((lang) => lang.code === selectedLang) || null}
            onChange={(_, newValue) => {
              setSelectedLang(newValue ? newValue.code : "");
            }}
            renderInput={(params) => <TextField {...params} size="small" />}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Button
            variant="contained"
            color="success"
            sx={{
              bgcolor: "#00573D",
              fontWeight: 600,
              fontSize: 14,
              py: 1.2,
              letterSpacing: 1,
              minWidth: 200,
              alignSelf: "center",
              mx: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": { bgcolor: "#004B2D" },
            }}
            onClick={handleLangSelect}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>SELECT</span>
              <ArrowForwardIosIcon
                sx={{ fontSize: 16, fontWeight: 600, ml: 0 }}
              />
            </span>
          </Button>
        </DialogActions>
      </Dialog>

      {/* Session Timeout Dialog */}
      <Dialog
        open={isUnauthorizedModalOpen}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <WarningIcon sx={{ fontSize: 48, color: "warning.main", mr: 1 }} />
          </Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            {t("sessionExpired", "Session Expired")}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("sessionExpiredMessage", "Your session has expired due to inactivity.")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("sessionExpiredLogoutMessage", "You will be logged out automatically. Please click \"OK\" to continue to the login page.")}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pt: 2 }}>
          <Button
            onClick={handleLogout}
            variant="contained"
            color="primary"
            size="large"
            sx={{ minWidth: 120 }}
          >
            {t('ok','OK')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;
